import { useState } from "react";
import type { BusinessContextView } from "../application/round02-service";
import { verifyCorrection } from "../application/round02-service";
import type { GovernanceOverview } from "../application/round03-service";
import type { CEOOSDatabase } from "../db/database";
import type {
  BehaviorCorrection,
  CEOProfile,
  DailyCEOReview,
  FounderBottleneckEntry,
  PromiseEntry,
} from "../domain/types";
import { safeErrorMessage } from "../privacy/policy";
import { ReviewIcon } from "../ui/icons";
import type { GovernanceArea } from "../ui/GovernanceTabs";

const FIELD_PROMPTS = {
  shenzhen: {
    label: "深圳模式",
    question: "今天我把什么不确定性变成了可验证结果？",
  },
  hong_kong: {
    label: "香港模式",
    question: "今天我增加了什么信任、准确性、合规性或可托付性？",
  },
  gba: {
    label: "大湾区模式",
    question: "今天我消除了哪一项跨境摩擦？",
  },
  custom: { label: "自定义场域", question: "" },
} as const;

type VerificationChoice =
  "completed" | "partially_completed" | "not_completed" | "blocked_external" | "unreasonable";

export function Today({
  database,
  profile,
  context,
  todayReview,
  todayCorrection,
  yesterdayCorrection,
  promise,
  bottleneck,
  online,
  governance,
  onStartReview,
  onStartVoiceSpike,
  onOpenGovernance,
  onRefresh,
}: {
  database: CEOOSDatabase;
  profile: CEOProfile;
  context?: BusinessContextView | undefined;
  todayReview?: DailyCEOReview | undefined;
  todayCorrection?: BehaviorCorrection | undefined;
  yesterdayCorrection?: BehaviorCorrection | undefined;
  promise?: PromiseEntry | undefined;
  bottleneck?: FounderBottleneckEntry | undefined;
  online: boolean;
  governance?: GovernanceOverview | undefined;
  onStartReview: () => void;
  onStartVoiceSpike: () => void;
  onOpenGovernance: (area: GovernanceArea) => void;
  onRefresh: () => Promise<void>;
}) {
  const [verificationChoice, setVerificationChoice] = useState<VerificationChoice>();
  const [verificationNote, setVerificationNote] = useState("");
  const [verificationLearning, setVerificationLearning] = useState("");
  const [continueNeeded, setContinueNeeded] = useState(false);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const field = FIELD_PROMPTS[profile.defaultFieldMode];
  const fieldQuestion =
    profile.defaultFieldMode === "custom"
      ? profile.customFieldQuestion || "今天我在当前场域中修正了什么可观察行为？"
      : field.question;
  const reviewLabel =
    todayReview?.reviewStatus === "exported"
      ? "今日复盘已完成并导出"
      : todayReview?.reviewStatus === "completed"
        ? "今日复盘已完成"
        : todayReview
          ? "今日复盘进行中"
          : "今天还没有完成复盘";

  const submitVerification = async () => {
    if (!yesterdayCorrection || !verificationChoice) return;
    setSaving(true);
    try {
      await verifyCorrection(database, yesterdayCorrection.id, verificationChoice, {
        ...(verificationChoice === "completed" || verificationChoice === "partially_completed"
          ? { evidence: verificationNote }
          : {}),
        ...(verificationChoice === "not_completed" ||
        verificationChoice === "blocked_external" ||
        verificationChoice === "unreasonable"
          ? { explanation: verificationNote }
          : {}),
        ...(verificationLearning ? { learning: verificationLearning } : {}),
        continueNeeded,
      });
      setNotice("昨天的纠偏验证已保存；系统不会自动滚动为今天的任务。");
      await onRefresh();
    } catch (error) {
      setNotice(safeErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="today-page">
      <header className="today-header">
        <div className="header-row">
          <span className="brand">CEO OS</span>
          <span className="connection-state">{online ? "本地可用" : "离线可用"}</span>
        </div>
        <h1>今天</h1>
        <p className="saved-line">
          {todayReview
            ? `本地已保存 · ${new Date(todayReview.updatedAt).toLocaleTimeString("zh-CN", {
                hour: "2-digit",
                minute: "2-digit",
              })}`
            : "本地数据库已就绪"}
        </p>
      </header>

      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}

      <button type="button" className="today-primary-action" onClick={onStartReview}>
        <ReviewIcon />
        {todayReview?.reviewStatus === "completed" || todayReview?.reviewStatus === "exported"
          ? "查看今日复盘结果"
          : todayReview
            ? "继续今日复盘"
            : "开始今日复盘"}
      </button>
      <div className="review-state-line">{reviewLabel}</div>

      <button type="button" className="voice-spike-entry" onClick={onStartVoiceSpike}>
        <span className="voice-spike-entry__icon" aria-hidden="true">
          ●
        </span>
        <span>
          <strong>开始实时语音技术验证</strong>
          <small>不保存音频或完整转写 · Round 03R-00A</small>
        </span>
      </button>

      {yesterdayCorrection && !yesterdayCorrection.verification && (
        <section className="priority-section" aria-labelledby="yesterday-correction">
          <h2 id="yesterday-correction">先检查昨天</h2>
          <span className="meta-line">昨天要修正：{yesterdayCorrection.behaviorToChange}</span>
          <p className="correction-copy">{yesterdayCorrection.action}</p>
          <div className="verification-options" role="group" aria-label="纠偏完成情况">
            {[
              ["completed", "已完成"],
              ["partially_completed", "部分完成"],
              ["not_completed", "未完成"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={verificationChoice === value ? "is-selected" : ""}
                onClick={() => setVerificationChoice(value as VerificationChoice)}
              >
                {label}
              </button>
            ))}
          </div>
          <details className="disclosure verification-details" open={Boolean(verificationChoice)}>
            <summary>补充证据、原因或学习</summary>
            <div className="minor-choice-row">
              <button
                type="button"
                className={verificationChoice === "blocked_external" ? "is-selected" : ""}
                onClick={() => setVerificationChoice("blocked_external")}
              >
                外部原因无法执行
              </button>
              <button
                type="button"
                className={verificationChoice === "unreasonable" ? "is-selected" : ""}
                onClick={() => setVerificationChoice("unreasonable")}
              >
                原纠偏不合理
              </button>
            </div>
            <label className="field">
              <span>可观察证据或原因</span>
              <textarea
                rows={2}
                value={verificationNote}
                onChange={(event) => setVerificationNote(event.target.value)}
              />
            </label>
            <label className="field">
              <span>学到了什么（可选）</span>
              <input
                value={verificationLearning}
                onChange={(event) => setVerificationLearning(event.target.value)}
              />
            </label>
            <label className="check-row">
              <input
                type="checkbox"
                checked={continueNeeded}
                onChange={(event) => setContinueNeeded(event.target.checked)}
              />
              <span>需要重新设计后再考虑延续；不要自动滚动</span>
            </label>
            <button
              type="button"
              className="primary-button"
              disabled={!verificationChoice || saving}
              onClick={() => void submitVerification()}
            >
              {saving ? "保存中…" : "记录验证"}
            </button>
          </details>
        </section>
      )}

      {yesterdayCorrection?.verification && (
        <section className="quiet-section">
          <h2>昨天的纠偏</h2>
          <p>{yesterdayCorrection.action}</p>
          <span className="meta-line">已记录验证：{yesterdayCorrection.verification.result}</span>
        </section>
      )}

      <section className="context-section" aria-labelledby="business-context">
        <h2 id="business-context">当前业务</h2>
        {context ? (
          <div className="context-rail">
            <div>
              <h3>180 天主线</h3>
              <p>{context.current180DayMainline || "尚未填写主线。"}</p>
            </div>
            <div>
              <h3>唯一瓶颈</h3>
              <p>{context.currentConstraint || "尚未记录当前瓶颈。"}</p>
            </div>
            {context.todayBusinessBattle && (
              <div>
                <h3>当前业务战役</h3>
                <p>{context.todayBusinessBattle}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state">
            <strong>还没有业务上下文</strong>
            <p>请在设置中导入 BoardContext，或手动填写最小业务背景。</p>
          </div>
        )}
      </section>

      <section className="field-prompt">
        <h2>
          {profile.defaultFieldMode === "custom"
            ? profile.customFieldName || field.label
            : field.label}
        </h2>
        <p>{fieldQuestion}</p>
      </section>

      {todayCorrection && (
        <section className="quiet-section">
          <h2>今日唯一行为纠偏</h2>
          <p>{todayCorrection.action}</p>
          <span className="meta-line">验证：{todayCorrection.verificationMetric}</span>
        </section>
      )}

      {governance && (
        <section className="today-governance" aria-labelledby="governance-risks">
          <div className="section-title-row">
            <h2 id="governance-risks">需要守住的边界</h2>
            <span>最多各 1 项</span>
          </div>
          {governance.promise.topRisk && (
            <button
              type="button"
              className="today-risk-row"
              onClick={() => onOpenGovernance("promise")}
            >
              <span className="risk-key">承诺</span>
              <span>
                <strong>{governance.promise.topRisk.content}</strong>
                <small>
                  {new Date(governance.promise.topRisk.dueAt).toLocaleString("zh-CN")} ·{" "}
                  {governance.promise.topRisk.promiseStatus}
                </small>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          )}
          {governance.focus.topRisk && (
            <button
              type="button"
              className="today-risk-row"
              onClick={() => onOpenGovernance("focus")}
            >
              <span className="risk-key">聚焦</span>
              <span>
                <strong>
                  {"deviationContent" in governance.focus.topRisk
                    ? governance.focus.topRisk.deviationContent
                    : governance.focus.topRisk.title}
                </strong>
                <small>查看停放、No-Start 与启动审查</small>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          )}
          {governance.truth.topRisk && (
            <button
              type="button"
              className="today-risk-row"
              onClick={() => onOpenGovernance("truth")}
            >
              <span className="risk-key">事实</span>
              <span>
                <strong>{governance.truth.topRisk.content}</strong>
                <small>{governance.truth.issues[0]?.message ?? "等待 Truth Inbox 复核"}</small>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          )}
          {!governance.promise.topRisk &&
            !governance.focus.topRisk &&
            !governance.truth.topRisk && (
              <p className="quiet-copy">当前没有由本地规则识别的治理风险。</p>
            )}
        </section>
      )}

      <details className="other-today">
        <summary>其他今日信息</summary>
        <div className="detail-list">
          <div>
            <strong>必须兑现的承诺</strong>
            <p>{promise ? promise.content : "当前没有待兑现承诺；系统不会制造警告。"}</p>
          </div>
          <div>
            <strong>创始人瓶颈</strong>
            <p>{bottleneck ? bottleneck.event : "当前没有已有瓶颈记录。"}</p>
          </div>
          <div>
            <strong>今日能量</strong>
            <p>
              {todayReview?.exportFieldSummaries?.energy_issue ??
                "在今日复盘中用一个 1—5 状态快速记录。"}
            </p>
          </div>
        </div>
      </details>
    </main>
  );
}
